import { useCallback, useState } from "react";
import { supportApi } from "./supportApi";
import { useDispatch } from "react-redux";
import {
  fetchMessagesFailed,
  fetchMessagesRequest,
  fetchMessagesSuccess,
} from "../../../../store/support/reducer";

interface UseSupportMessagesReturn {
  messages: any[];
  isLoading: boolean;
  error: any;
  fetchMessages: (
    chatId: string | number,
    options?: {
      force?: boolean;
      silent?: boolean;
      page?: number;
      per_page?: number;
      mode?: "replace" | "prepend";
    }
  ) => Promise<any[]>;
  sendMessage: (chatId: string | number, payload: any) => Promise<any>;
  markRead: (chatId: string | number, payload?: any) => Promise<any>;
}

export const useSupportMessages = (): UseSupportMessagesReturn => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const messages: any[] = [];

  const fetchMessages = useCallback(async (
    chatId: string | number,
    options: {
      force?: boolean;
      silent?: boolean;
      page?: number;
      per_page?: number;
      mode?: "replace" | "prepend";
    } = {}
  ) => {
    setIsLoading(true);
    setError(null);
    dispatch(fetchMessagesRequest({ chatId, silent: options.silent }));
    try {
      const resp = await supportApi.listMessages(chatId, {
        page: options.page,
        per_page: options.per_page,
      });
      const data: any = resp?.data;
      const meta = data?.meta ?? resp?.data?.meta ?? null;
      const results =
        data?.data ??
        data?.results ??
        data?.result ??
        data ??
        [];
      const safe = Array.isArray(results) ? results : [];
      dispatch(
        fetchMessagesSuccess({
          chatId,
          messages: safe,
          fetchedAt: Date.now(),
          mode: options.mode,
          meta,
        })
      );
      return safe;
    } catch (e: any) {
      setError(e);
      dispatch(fetchMessagesFailed({ chatId, error: e }));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const sendMessage = useCallback(async (chatId: string | number, payload: any) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp: any = await supportApi.sendMessage(chatId, payload);
      return resp?.data;
    } catch (e: any) {
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const markRead = useCallback(async (chatId: string | number, payload: any = {}) => {
    const silent = Boolean(payload?.silent);
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const resp: any = await supportApi.markRead(chatId, payload);
      return resp?.data;
    } catch (e: any) {
      if (!silent) setError(e);
      throw e;
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  return {
    messages,
    isLoading,
    error,
    fetchMessages,
    sendMessage,
    markRead,
  };
};
